import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  UsersIcon, 
  EyeIcon, 
  GlobeAltIcon,
  ClockIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { authFetch } from '@/utils/api'

interface SessionData {
  id: string
  session_id?: string
  ip_address?: string
  ip?: string
  userAgent?: string
  startTime?: string
  first_seen?: string
  lastActivity?: string
  last_seen?: string
  pagesVisited?: number
  page_count?: number
  documentsDownloaded?: number
  interaction_count?: number
  total_requests?: number
  session_duration?: number
  status?: string
}

const SessionsPage = () => {
  const [sessions, setSessions] = useState<SessionData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('lastActivity')

  // Fetch sessions data
  const fetchSessions = async () => {
    try {
      const response = await authFetch('/api/monitoring/sessions')
      if (!response.ok) throw new Error('Failed to fetch sessions')
      const result = await response.json()
      
      if (result.success) {
        setSessions(result.data.sessions || [])
      }
    } catch (err) {
      console.error('Error fetching sessions:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await fetchSessions()
      setLoading(false)
    }
    
    loadData()
    
    // Refresh every 10 seconds
    const interval = setInterval(fetchSessions, 10000)
    return () => clearInterval(interval)
  }, [])

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${Math.round(seconds / 3600)}h`
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'inactive': return 'bg-gray-100 text-gray-800'
      case 'suspicious': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = searchTerm === '' || 
      (session.ip_address || session.ip || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (session.userAgent || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (session.session_id || session.id || '').toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || session.status === statusFilter
    
    return matchesSearch && matchesStatus
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-800 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sessions...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Session Management - CyberScrape Training Platform</title>
        <meta name="description" content="Detailed session management and monitoring for instructor oversight" />
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Session Management</h1>
            <p className="text-sm text-gray-600">
              Monitor and manage user sessions in real-time
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-600">Auto-refreshing</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by IP, User Agent, or Session ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspicious">Suspicious</option>
              </select>
            </div>

            {/* Sort */}
            <div className="sm:w-48">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
              >
                <option value="lastActivity">Last Activity</option>
                <option value="startTime">Start Time</option>
                <option value="duration">Duration</option>
                <option value="requests">Total Requests</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sessions List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Active Sessions ({filteredSessions.length})
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {filteredSessions.map((session, index) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-6 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Session Header */}
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${
                          new Date(session.last_seen || session.lastActivity || '').getTime() > Date.now() - 30 * 60 * 1000 
                            ? 'bg-green-500' 
                            : 'bg-gray-400'
                        }`}></div>
                        <span className="font-semibold text-gray-900">
                          {session.ip_address || session.ip || 'Unknown IP'}
                        </span>
                      </div>
                      
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(session.status || 'unknown')}`}>
                        {session.status || 'Unknown'}
                      </span>
                    </div>

                    {/* Session Details */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <EyeIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-600">Pages</p>
                          <p className="font-medium">{session.page_count || session.pagesVisited || 0}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <ComputerDesktopIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-600">Interactions</p>
                          <p className="font-medium">{session.interaction_count || 0}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <GlobeAltIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-600">Requests</p>
                          <p className="font-medium">{session.total_requests || 0}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-600">Duration</p>
                          <p className="font-medium">{formatDuration(session.session_duration || 0)}</p>
                        </div>
                      </div>
                    </div>

                    {/* User Agent */}
                    <div className="mb-3">
                      <p className="text-xs text-gray-600 mb-1">User Agent:</p>
                      <p className="text-sm text-gray-800 font-mono bg-gray-100 px-2 py-1 rounded">
                        {session.userAgent || 'Unknown'}
                      </p>
                    </div>

                    {/* Timestamps */}
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>
                        Started: {session.first_seen || session.startTime ? 
                          new Date(session.first_seen || session.startTime || '').toLocaleString() : 
                          'Unknown'
                        }
                      </span>
                      <span>
                        Last Activity: {session.last_seen || session.lastActivity ? 
                          new Date(session.last_seen || session.lastActivity || '').toLocaleString() : 
                          'Unknown'
                        }
                      </span>
                    </div>

                    {/* Session ID */}
                    <div className="mt-2">
                      <span className="text-xs text-gray-500">
                        Session: {(session.session_id || session.id || 'unknown').toString().slice(-16)}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
            
            {filteredSessions.length === 0 && (
              <div className="text-center py-12">
                <UsersIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  {searchTerm || statusFilter !== 'all' ? 'No sessions match your filters' : 'No active sessions'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default SessionsPage
